import React, { useEffect, useState } from "react";
import { Col, Form } from "react-bootstrap";
import Select from "react-select";
import AsyncCreatableSelect from "react-select/async-creatable";
import api from "../../../api";

// Operational context of a monthly entry (Phase 65): Schools, Category,
// Subcategories and Feedback recipients. Which of these appear is decided by
// the KPI's configured entry fields (role_in_calculation = CONTEXT), never by
// the KPI name. Options come from the existing masters the Data Analyst
// already uses for Own Ticket (/schools, /categories/technician-owned,
// /subcategories/technician-owned/:id); every id is re-checked server-side.
//
// `value` = { [field_key]: id | id[] }; `onChange(next)` replaces it.

const toOption = (row) => ({ value: Number(row.id), label: row.name });
const byId = (options, ids) => (ids || []).map((id) => options.find((o) => o.value === Number(id))).filter(Boolean);

const FIELD_TYPES = { SCHOOL_MULTI: "SCHOOL_MULTI", CATEGORY: "CATEGORY", SUBCATEGORY_MULTI: "SUBCATEGORY_MULTI", USER_MULTI: "USER_MULTI" };

// Feedback recipients are registered users ({ user_id }) or plain email
// addresses ({ email }) for people without an account. Same syntax rule as
// the server (services/kpiManualEvidenceContextService.js): local@domain.tld.
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/;
export const isValidEmail = (raw) => EMAIL_FORMAT.test(String(raw || "").trim());
const recipientKey = (r) => (r.user_id ? `u:${r.user_id}` : `e:${String(r.email).trim().toLowerCase()}`);
const userOption = (u) => ({ value: `u:${u.id}`, label: u.email ? `${u.name} (${u.email})` : u.name, user_id: Number(u.id), email: u.email || null });
const emailOption = (email) => ({ value: `e:${email.trim().toLowerCase()}`, label: email.trim(), email: email.trim() });

export function contextFieldsOf(fields) {
  return (fields || []).filter((f) => f.role_in_calculation === "CONTEXT" && FIELD_TYPES[f.field_type]);
}

// First missing required context field's message, or "".
//
// One control per field TYPE: the form renders only the first field of each
// type (EntryContextFields below), and the server validates exactly that
// field's value and checks completeness per type
// (kpiManualEvidenceContextService: validateContext/fieldOfType,
// isContextComplete, assertContextComplete). A required type is therefore
// satisfied by the value of the field that is actually on screen -- checking
// every field KEY instead made a stale duplicate (e.g. the old Phase 65 seed's
// `feedback_recipient_ids` beside `feedback_recipients`) impossible to fill
// and kept Submit disabled on a fully populated form.
export function contextValidationError(fields, value) {
  const ctxFields = contextFieldsOf(fields);
  const checked = new Set();
  for (const f of ctxFields) {
    if (!f.is_required || checked.has(f.field_type)) continue;
    checked.add(f.field_type);
    const shown = ctxFields.find((x) => x.field_type === f.field_type);
    const v = value[shown.field_key];
    const empty = f.field_type === FIELD_TYPES.CATEGORY ? !v : !Array.isArray(v) || v.length === 0;
    if (empty) return f.field_type === FIELD_TYPES.CATEGORY ? `Select a ${shown.field_label.toLowerCase()}.` : `Select at least one of "${shown.field_label}".`;
  }
  return "";
}

function Label({ field }) {
  return <Form.Label htmlFor={`kp-ctx-${field.field_key}`}>{field.field_label} {field.is_required && <span className="text-danger">*</span>}</Form.Label>;
}

const EntryContextFields = ({ kpiId, month, fields, value, onChange, initialRecipients = [] }) => {
  const ctxFields = contextFieldsOf(fields);
  const fieldOf = (type) => ctxFields.find((f) => f.field_type === type) || null;
  const schoolField = fieldOf(FIELD_TYPES.SCHOOL_MULTI);
  const categoryField = fieldOf(FIELD_TYPES.CATEGORY);
  const subField = fieldOf(FIELD_TYPES.SUBCATEGORY_MULTI);
  const userField = fieldOf(FIELD_TYPES.USER_MULTI);

  const [schools, setSchools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  // Every recipient option seen so far, keyed by recipientKey -- so a chip
  // keeps its label after the search results change.
  const [recipientOptions, setRecipientOptions] = useState(() => new Map(initialRecipients.map((r) => {
    const o = r.user_id ? userOption({ id: r.user_id, name: r.name, email: r.email }) : emailOption(r.email);
    return [o.value, o];
  })));
  const remember = (options) => setRecipientOptions((prev) => {
    const next = new Map(prev);
    options.forEach((o) => next.set(o.value, o));
    return next;
  });
  const categoryId = categoryField ? value[categoryField.field_key] || null : null;

  useEffect(() => {
    if (!schoolField) return;
    api.get("/schools").then((res) => setSchools((Array.isArray(res.data) ? res.data : res.data?.data || []).map(toOption))).catch(() => setSchools([]));
  }, [schoolField?.field_key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!categoryField) return;
    api.get("/categories/technician-owned").then((res) => setCategories((Array.isArray(res.data) ? res.data : []).map(toOption))).catch(() => setCategories([]));
  }, [categoryField?.field_key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!subField || !categoryId) { setSubcategories([]); return; }
    let cancelled = false;
    api.get(`/subcategories/technician-owned/${categoryId}`)
      .then((res) => { if (!cancelled) setSubcategories((Array.isArray(res.data) ? res.data : []).map(toOption)); })
      .catch(() => { if (!cancelled) setSubcategories([]); });
    return () => { cancelled = true; };
  }, [subField?.field_key, categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, next) => onChange({ ...value, [key]: next });

  const changeCategory = (opt) => {
    const next = { ...value, [categoryField.field_key]: opt ? opt.value : null };
    // Subcategories belong to one category: a different category makes every
    // selected subcategory incompatible, so they are cleared.
    if (subField && (opt ? opt.value : null) !== categoryId) next[subField.field_key] = [];
    onChange(next);
  };

  const loadRecipients = (input) => api
    .get(`/performance/my-kra-kpi/${kpiId}/feedback-recipients`, { params: { search: input || undefined, month } })
    .then((res) => {
      const options = (res.data?.data || []).map(userOption);
      remember(options);
      return options;
    })
    .catch(() => []);

  const selectedRecipients = userField ? (value[userField.field_key] || []) : [];
  const selectedRecipientOptions = selectedRecipients
    .map((r) => recipientOptions.get(recipientKey(r)) || (r.email ? emailOption(r.email) : null))
    .filter(Boolean);

  // A typed address can be added when it is a valid email that is not
  // already selected or offered as a registered user (case-insensitive).
  const canCreateRecipient = (input, selected, options) => {
    if (!isValidEmail(input)) return false;
    const key = input.trim().toLowerCase();
    const taken = (o) => (o.email && o.email.toLowerCase() === key) || o.value === `e:${key}`;
    return !selected.some(taken) && !options.some(taken);
  };

  const changeRecipients = (opts) => {
    const seen = new Set();
    const next = [];
    for (const o of opts || []) {
      const key = o.email ? o.email.toLowerCase() : o.value;
      if (seen.has(key)) continue; // same person picked twice (by name and by email)
      seen.add(key);
      next.push(o.user_id ? { user_id: o.user_id } : { email: o.email });
    }
    remember((opts || []).map((o) => (o.user_id ? o : emailOption(o.email))));
    set(userField.field_key, next);
  };

  if (!ctxFields.length) return null;

  return (
    <>
      {schoolField && (
        <Form.Group as={Col} md={6}>
          <Label field={schoolField} />
          <Select
            inputId={`kp-ctx-${schoolField.field_key}`} classNamePrefix="kp-select" isMulti placeholder="+ Select School"
            options={schools} value={byId(schools, value[schoolField.field_key])}
            onChange={(opts) => set(schoolField.field_key, (opts || []).map((o) => o.value))}
          />
        </Form.Group>
      )}
      {categoryField && (
        <Form.Group as={Col} md={6}>
          <Label field={categoryField} />
          <Select
            inputId={`kp-ctx-${categoryField.field_key}`} classNamePrefix="kp-select" isClearable placeholder="Select Category"
            options={categories} value={categories.find((o) => o.value === Number(categoryId)) || null}
            onChange={changeCategory}
          />
        </Form.Group>
      )}
      {subField && (
        <Form.Group as={Col} md={6}>
          <Label field={subField} />
          <Select
            inputId={`kp-ctx-${subField.field_key}`} classNamePrefix="kp-select" isMulti
            isDisabled={categoryField ? !categoryId : false}
            placeholder={categoryField && !categoryId ? "Select a category first" : "+ Select Subcategory"}
            options={subcategories} value={byId(subcategories, value[subField.field_key])}
            onChange={(opts) => set(subField.field_key, (opts || []).map((o) => o.value))}
          />
        </Form.Group>
      )}
      {userField && (
        <Form.Group as={Col} md={6}>
          <Label field={userField} />
          <AsyncCreatableSelect
            inputId={`kp-ctx-${userField.field_key}`} classNamePrefix="kp-select" isMulti cacheOptions defaultOptions
            placeholder="Search by name, or type an email address" loadOptions={loadRecipients}
            value={selectedRecipientOptions}
            onChange={changeRecipients}
            isValidNewOption={canCreateRecipient}
            // The menu row reads + Add "<email>"; once picked, the chip shows the
            // plain address (changeRecipients stores emailOption()).
            getNewOptionData={(input) => ({ ...emailOption(input), label: `+ Add "${input.trim()}"` })}
            createOptionPosition="first"
            noOptionsMessage={({ inputValue }) => (inputValue && inputValue.includes("@") && !isValidEmail(inputValue)
              ? "Enter a valid email address"
              : inputValue ? "No matching users — type a full email address to add it" : "Type a name or an email address")}
          />
          <Form.Text muted>
            Pick a registered user or type any email address. Each person gets this month&apos;s User Feedback form by email and must enter that same address to answer it.
          </Form.Text>
        </Form.Group>
      )}
    </>
  );
};

export default EntryContextFields;
